<script>
export default {
	name: 'Accordion',
	inheritAttrs: false,
};
</script>

<script setup>
import FontAwesomeLazy from '~/components/FontAwesomeLazy.vue';
import TransitionSimpleSlideDown from '~/components/transition/TransitionSimpleSlideDown.vue';
import Parsys from '~/components/Parsys.vue';
import { getCompositeComponent } from '@custom/aem-vue-editable-components';
import ExperienceFragment from '~/aem-components/ExperienceFragment.vue';

const { isEditorMode } = useGlobalAEMState();

const props = defineProps({
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	type: { type: String, default: 'parent' }, // <== 'parent' || 'single'
	accordionStyle: { type: String, default: 'light-grey' }, // <== 'white' || 'light-grey'
	parentPageUrl: { type: String, default: '' },
	children: { type: Array, default: () => [] },
	accordionTitle: { type: String, default: '' },
	accordionDescription: { type: String, default: '' },
	componentIdAA: { type: String, default: '' },
	isExpandOnPageLoad: { type: Boolean, default: false },
});

const expandedList = ref(new Set());

const accordionParsys = getCompositeComponent({
	type: 'mh/components/mh-layout-container',
	componentKey: 'accordion-parsys',
});

const expandItem = (itemId) => {
	expandedList.value.add(itemId);
};
const collapseItem = (itemId) => {
	expandedList.value.delete(itemId);
};

const computedListMap = computed(() => {
	if (props.type === 'single') {
		return [{
			id: props.accordionTitle,
			title: props.accordionTitle,
			description: props.accordionDescription,
			expanded: isEditorMode.value ? true : expandedList.value.has(props.accordionTitle),
		}];
	}
	return props.children.map((page) => {
		return {
			id: page.accordionUrl,
			title: page.accordionTitle,
			description: page.accordionDescription,
			collapedContent: page.accordionUrl,
			expanded: isEditorMode.value ? true : expandedList.value.has(page.accordionUrl),
		};
	});
});

onMounted(() => {
	if (props.isExpandOnPageLoad && props.type === 'single') {
		expandedList.value.add(props.accordionTitle);
	}
});

</script>

<template>

<div
	v-if="computedListMap.length"
	class="Accordion"
	:class="{
		'>md:hidden': isHideInDesktop,
		'hidden >md:block': isHideInMobile,
		'use-white-version': accordionStyle === 'white',
	}"
>

	<div v-for="block in computedListMap" :key="block.id" class="Accordion-block">
		<div
			v-aa="[
				{
					clickName: block.title,
					clickComponentType: 'Button',
					componentName: 'Accordion',
					componentID: props.componentIdAA,
				},
				{
					name: block.title,
					type: 'other',
				},
			]"
			class="heading"
			@click="block.expanded ? collapseItem(block.id) : expandItem(block.id)"
		>
			<div>
				<h4
					class="title"
					:class="[
						{ 'line-clamp-1 md:line-clamp-4': !block.expanded },
					]"
				>{{ block.title }}</h4>
				<p
					class="text-sm text-neutral-grey-ultradark"
					:class="[
						{ 'line-clamp-1 md:line-clamp-2': !block.expanded },
					]"
				>{{ block.description }}</p>
			</div>
			<div>
				<icon-fal-chevron-down
					class="icon-arrow transform"
					:class="{ 'rotate-180': block.expanded }"
				/>
			</div>
		</div>
		<TransitionSimpleSlideDown>
			<div v-show="block.expanded" class="content">
				<div class="px-4 pb-4">
					<div v-if="type === 'parent'">
						<ExperienceFragment
							:xfPath="block.collapedContent"
							:showSpinner="true"
						/>
					</div>

					<div v-else-if="type === 'single'">
						<Parsys
							:component="accordionParsys"
							label="Accordion Content"
						/>
					</div>
				</div>
			</div>
		</TransitionSimpleSlideDown>
		
	</div>
</div>

<div v-else-if="isEditorMode && isHideInDesktop" class="generic-container py-10 text-center">
	<p>Hidden in: <span v-if="isHideInDesktop">Desktop</span> <span v-if="isHideInMobile">, Mobile</span></p>
</div>

</template>



<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.Accordion {
	max-width: calc(10/12 * #{var.$breakpoint-2xl}px);
	@media #{var.$query-max-2xl} { max-width: calc(10/12 * #{var.$breakpoint-xl}px) }
	@media #{var.$query-max-xl} { max-width: calc(10/12 * #{var.$breakpoint-lg}px) }
	@media #{var.$query-max-lg} { max-width: none }

	@apply mx-auto;
	.heading {
		display: flex;
		justify-content: space-between;
		align-items: center;
		cursor: pointer;
		@apply p-4;
	}

	.content {
		transition: 200ms ease-out;
		overflow: hidden;
	}
}

.use-white-version {
	.Accordion-block {
		background-color: var(--neutral-white-base);
		border-color: var(--neutral-grey-light);
		&:hover {
			background-color: var(--neutral-grey-ultralight);
			border-color: var(--neutral-grey-base);
		}
	}
}

.Accordion-block {
	background-color: var(--neutral-grey-ultralight);
	border: 1px solid var(--neutral-grey-light);
	@apply rounded-xl mb-4;
	&:hover {
		background-color: var(--neutral-grey-extralight);
		border-color: var(--neutral-grey-base);
		transition: all ease 200ms;
	}
}

.title {
	@apply text-base font-semibold;
}

.icon-arrow {
	width: 14px;
	transition: all ease 200ms;
	transform-origin: center;
	fill: var(--primary-black-base);
}

</style>
